import { api, wire } from "lwc";

import type {
  CollectionConfig,
  CollectionQuery,
} from "common/contentCollection";
import type { CollectionChangeEvent } from "common/contentCollection";
import { createTrack } from "common/track";
import {
  trackViewEvent,
  trackUxEvent,
} from "@devforce/trailhead-client-tracking";
import type { ContentSummary } from "common/contentSummary";
import { UxEventKeyType } from "@devforce/trailhead-client-gql";
import { EffectAdapter } from "common/effectAdapter";

import type { UserActivityEvent, UserActivityEvents } from "common/track";

import External from "thexternal/base";

import {
  LearningCollectionItem,
  CommunityCollectionItem,
  LearningQuery,
  CommunityQuery,
} from "./queries";
import { CtaEvent } from "tds/card";

export default class Collection extends External {
  @api config: CollectionConfig | null = null;
  @api items:
    | LearningCollectionItem[]
    | CommunityCollectionItem[]
    | null = null;
  @api variant: "learning" | "community" = "learning";

  private track = createTrack<"thexternal-collection">(this);

  private get learningItems() {
    return (this.items as LearningCollectionItem[]) ?? [];
  }

  private get communityItems() {
    return (this.items as CommunityCollectionItem[]) ?? [];
  }

  private get query(): CollectionQuery<unknown> | undefined {
    switch (this.variant) {
      case "learning":
        return new LearningQuery({
          learningItems: this.learningItems,
          limit: this.config?.limit ?? 3,
        });
      case "community":
        return new CommunityQuery({
          communityItems: this.communityItems,
          limit: this.config?.limit ?? 3,
        });
      default:
        return undefined;
    }
  }

  private get itemIds(): string {
    switch (this.variant) {
      case "learning":
        return this.learningItems.map((item) => item.name).join(",");
      case "community":
        return this.communityItems.join(",");
      default:
        return "";
    }
  }

  private handleTrailheadTrack(e: UserActivityEvent<keyof UserActivityEvents>) {
    e.stopPropagation();
  }

  private handleNavigate(e: CustomEvent<ContentSummary>) {
    const content = e.detail;
    trackViewEvent({
      id: content.id,
      name: content.id,
      url: content.path,
      eventName: `${content.type}_clicked`,
      // @ts-ignore
      type: content.type.toUpperCase(),
      namespace: "",
      parentName: null,
      parentType: null,
    });

    this.track("navigate", { content: e.detail });
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  private handleCta(e: CtaEvent) {
    this.track("cta_clicked", { content: e.detail });
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  private handleStateChange(e: CollectionChangeEvent) {
    switch (e.detail.state) {
      case "error":
        return this.track("error", undefined);
      case "empty":
        return this.track("empty", undefined);
      case "loading":
        return this.track("loading", undefined);
      case "loaded":
        return this.track("loaded", { content: e.detail.items || [] });
      default:
        return "";
    }
  }

  @wire(EffectAdapter, { config: "$config" })
  private onConfigUpdate() {
    this.template.host.setAttribute(
      "data-card-layout",
      this.config?.card?.layout ?? ""
    );
    this.template.host.setAttribute(
      "data-card-variant",
      this.config?.card?.variant ?? ""
    );
  }

  connectedCallback() {
    super.connectedCallback();
    trackUxEvent({
      key: UxEventKeyType.MfeDisplayedType,
      value: this.itemIds,
    });
  }
}
